import { RubyLoader } from '../../../components/RubyLoader';
/**
 * Tier 3 confirmation — the heart of the design
 * (`fleet-device-support.html:202-239`). Nothing here calls `onRun`/`onConfirm`
 * on a guess: the blast radius comes from a live probe, the code comes from a
 * server-issued, single-use, device+action+reason-bound grant, and the Fire
 * button stays disabled until every one of those is genuinely satisfied.
 *
 * Order, per the mockup and Task 21's brief:
 *  1. Blast radius, named first — money and count, from `GET
 *     /devices/{deviceId}/impact`. A failed probe disables Fire outright; it
 *     is never treated as "nothing to lose".
 *  2. Step 1 — typed register name, EXACT match.
 *  3. Reason — `SearchableDropdown`, the mockup's five reasons.
 *  4. Step 2 — the ruby code panel: Send code / 6-char mono input / live
 *     "Sent to N addresses · expires in m:ss · single use".
 *
 * A STALE impact (offline device) requires a SECOND explicit confirmation —
 * a custom toggle, not a native checkbox styled away. On code expiry the
 * field clears and a re-send is offered. Changing the reason after a code was
 * issued invalidates it — a fresh reason needs a fresh code, because the code
 * authorises THIS wipe for THIS reason. A 409 `device_offline` refusal from
 * the server hides the Fire button and offers "Arm on next contact" as its
 * own, separately-clicked, separately-coded action — never a silent queue.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Clock, Send } from 'lucide-react';
import type { FleetActionId, FleetImpact } from '@platform/shared-types';
import { formatBlastRadius } from '@platform/shared-types';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { fleetSupportApi, FleetApiError } from '../../../services/fleetSupportApi';
import { FLEET_UI_ACTIONS } from './actionRegistry';
import type { FleetRow } from './fleetRows';

/** The footer button's verb per action — "Wipe this till" is the mockup's own copy for factory_wipe. */
const FIRE_LABEL: Partial<Record<FleetActionId, string>> = {
  factory_wipe: 'Wipe this till',
  clear_pairing: 'Clear pairing',
};

const REASONS = [
  'Returned to stock',
  'Re-assigning to another merchant',
  'Non-payment / recovery',
  'Hardware fault / RMA',
  'Lost or stolen',
];
const REASON_OPTIONS = REASONS.map((r) => ({ value: r, label: r }));

export interface DestructiveConfirmBody {
  reason: string;
  codeId: string;
  code: string;
  params?: Record<string, unknown>;
}

export interface DestructiveConfirmProps {
  row: FleetRow;
  action: FleetActionId;
  busy?: boolean;
  /** The device's own failure reason from its most recent attempt at THIS action, verbatim — never paraphrased. */
  lastFailureReason?: string | null;
  onCancel: () => void;
  /** Rejects with the same `FleetApiError` the API client throws — this component reads `.code === 'device_offline'` off it to switch into the "Arm on next contact" state. */
  onConfirm: (body: DestructiveConfirmBody) => Promise<void>;
}

function mmss(msRemaining: number): string {
  const s = Math.max(0, Math.ceil(msRemaining / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

export default function DestructiveConfirm({
  row, action, busy, lastFailureReason, onCancel, onConfirm,
}: DestructiveConfirmProps) {
  const registerName = row.registerName || row.deviceId || 'this till';
  const actionLabel = FLEET_UI_ACTIONS.find((a) => a.id === action)?.label || action.replace(/_/g, ' ');
  const deviceId = row.deviceId;

  // --- Blast radius ---------------------------------------------------
  const [impact, setImpact] = useState<FleetImpact | null>(null);
  const [impactStale, setImpactStale] = useState(false);
  const [impactLoading, setImpactLoading] = useState(true);
  const [impactError, setImpactError] = useState<string | null>(null);

  useEffect(() => {
    if (!deviceId) { setImpactError('No device id to probe.'); setImpactLoading(false); return; }
    let live = true;
    setImpactLoading(true);
    setImpactError(null);
    fleetSupportApi.getImpact(deviceId)
      .then((res) => {
        if (!live) return;
        setImpact(res.impact);
        setImpactStale(!!res.stale || !!res.impact?.stale);
      })
      .catch((e: any) => {
        if (!live) return;
        setImpactError(e?.message || 'Could not verify what this action would destroy');
      })
      .finally(() => { if (live) setImpactLoading(false); });
    return () => { live = false; };
  }, [deviceId]);

  const blastLines = useMemo(() => (impact ? formatBlastRadius(impact) : []), [impact]);

  // --- Step 1: typed name ----------------------------------------------
  const [typedName, setTypedName] = useState('');
  const nameMatches = typedName === registerName;

  // --- Reason ------------------------------------------------------------
  const [reason, setReason] = useState<string | null>(null);
  const [reasonAtIssue, setReasonAtIssue] = useState<string | null>(null);

  // --- Second confirmation for a stale figure -----------------------------
  const [staleAck, setStaleAck] = useState(false);

  // --- Step 2: code --------------------------------------------------------
  const [codeId, setCodeId] = useState<string | null>(null);
  const [issuedTo, setIssuedTo] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (!expiresAt) return;
    tickRef.current = window.setInterval(() => setNow(Date.now()), 1000);
    return () => { if (tickRef.current) window.clearInterval(tickRef.current); };
  }, [expiresAt]);

  const expired = !!expiresAt && now >= expiresAt;

  // Code expires -> clear the field and explain, per the brief.
  useEffect(() => {
    if (expired && code) setCode('');
  }, [expired]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reason changed after a code was issued -> invalidate, require re-issue.
  useEffect(() => {
    if (codeId && reasonAtIssue !== null && reason !== reasonAtIssue) {
      setCodeId(null);
      setIssuedTo([]);
      setExpiresAt(null);
      setCode('');
      setCodeError('Reason changed — the code was invalidated. Send a new one.');
    }
  }, [reason]); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCode = async () => {
    if (!deviceId || !reason) return;
    setSending(true);
    setCodeError(null);
    try {
      const res = await fleetSupportApi.issueAuthCode({ deviceId, action, reason });
      setCodeId(res.codeId);
      setIssuedTo(res.issuedTo || []);
      setExpiresAt(res.expiresAt);
      setReasonAtIssue(reason);
      setCode('');
    } catch (e: any) {
      setCodeError(e?.message || 'Could not send the code');
    } finally {
      setSending(false);
    }
  };

  // --- Fire ------------------------------------------------------------
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [offlineRefused, setOfflineRefused] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const impactVerified = !impactLoading && !impactError && !!impact;
  const staleGateOk = !impactStale || staleAck;
  const codeReady = !!codeId && code.length === 6 && !expired;
  const canFire = impactVerified && nameMatches && !!reason && staleGateOk && codeReady && !offlineRefused;

  const fire = async (armOnNextContact: boolean) => {
    if (!reason || !codeId) return;
    if (!armOnNextContact && !canFire) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onConfirm({
        reason, codeId, code,
        ...(armOnNextContact ? { params: { armOnNextContact: true } } : {}),
      });
    } catch (e: any) {
      if (e instanceof FleetApiError && e.code === 'device_offline') {
        setOfflineRefused(true);
      } else {
        setSubmitError(e?.message || 'Could not send that command');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = !!busy || submitting;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4" onClick={onCancel} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${actionLabel} — ${registerName}`}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg overflow-hidden rounded-xl border border-rose-600 bg-white shadow-2xl"
      >
        <div className="flex items-center gap-2 bg-rose-600 px-4 py-2.5 text-[13px] font-bold text-white">
          <AlertTriangle size={16} />
          {actionLabel} — {registerName} · two steps
        </div>

        <div className="flex flex-col gap-3 p-4">
          {lastFailureReason && (
            <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Last attempt failed: {lastFailureReason}
            </div>
          )}

          {/* --- Blast radius, named first --- */}
          <div className="rounded-lg border border-rose-600 bg-rose-50 px-3 py-2.5">
            <div className="mb-1.5 text-[12px] font-bold text-rose-700">What this destroys, checked just now</div>
            {impactLoading && <RubyLoader variant="inline" size={14} label="Checking the device…" />}
            {impactError && (
              <div role="alert" className="text-[12.5px] font-semibold text-rose-700">
                {impactError} — this action stays disabled until it can be verified.
              </div>
            )}
            {!impactLoading && !impactError && impact && (
              <>
                <ul className="list-disc space-y-1 pl-4 text-[12.5px] text-gray-900">
                  {blastLines.map((l) => <li key={l}>{l}</li>)}
                </ul>
                {impactStale && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11.5px] font-semibold text-amber-800">
                    <Clock size={13} className="mt-0.5 shrink-0" />
                    This is the last known figure, not a live one — the device is offline. It may be wrong.
                  </div>
                )}
              </>
            )}
          </div>

          {impactStale && !impactLoading && !impactError && (
            <button
              type="button"
              onClick={() => setStaleAck((v) => !v)}
              className={`flex min-h-[44px] items-center gap-2 rounded-lg border px-3 py-2 text-left text-[12px] font-semibold ${
                staleAck ? 'border-amber-500 bg-amber-50 text-amber-800' : 'border-gray-200 bg-white text-gray-600'
              }`}
              aria-pressed={staleAck}
            >
              <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${staleAck ? 'border-amber-600 bg-amber-500 text-white' : 'border-gray-300'}`}>
                {staleAck ? '✓' : ''}
              </span>
              I understand this figure may be out of date, and want to proceed anyway
            </button>
          )}

          {/* --- Step 1: typed name --- */}
          <div>
            <label className="mb-1 block text-[11.5px] font-semibold text-gray-600">Step 1 · Type the register name to confirm</label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={registerName}
              className="h-[44px] w-full rounded-lg border border-gray-300 px-3 font-mono text-[13px] text-gray-900"
            />
          </div>

          {/* --- Reason --- */}
          <SearchableDropdown
            label="Reason — goes in the audit trail"
            options={REASON_OPTIONS}
            value={reason}
            onChange={setReason}
            placeholder="Choose…"
          />

          {/* --- Step 2: code --- */}
          <div className="rounded-lg border border-rose-200 bg-rose-50/60 px-3 py-2.5" style={{ borderColor: '#FECDD3', background: '#FFF1F3' }}>
            <div className="mb-1.5 flex items-center gap-2 text-[12px] font-bold" style={{ color: '#9F1239' }}>
              <span className="rounded px-1.5 py-0.5 font-mono text-[10px] text-white" style={{ background: '#DC143C' }}>Step 2</span>
              Authorisation code
            </div>
            <p className="mb-2 text-[11.5px] leading-relaxed text-gray-600">
              Sent to the <b>company notification emails</b> on <code>/legal/company</code>. The code authorises{' '}
              <b>this {actionLabel.toLowerCase()}, of this device, for this reason</b> — it cannot be reused on another till or for another action.
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="text"
                maxLength={6}
                value={code}
                disabled={!codeId || expired}
                onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
                placeholder="······"
                className="h-[44px] flex-1 rounded-lg border text-center font-mono text-[16px] tracking-[0.5em] text-gray-900 disabled:bg-gray-100"
                style={{ borderColor: '#FECDD3' }}
              />
              <button
                type="button"
                onClick={sendCode}
                disabled={!reason || sending || !deviceId}
                className="flex min-h-[44px] items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-bold text-white disabled:opacity-50"
                style={{ background: '#DC143C' }}
              >
                <Send size={14} />
                {sending ? 'Sending…' : codeId ? 'Resend code' : 'Send code'}
              </button>
            </div>
            {codeError && <p role="alert" className="mt-1.5 text-[11.5px] font-semibold text-rose-700">{codeError}</p>}
            {expired && !codeError && (
              <p role="alert" className="mt-1.5 text-[11.5px] font-semibold text-rose-700">
                This code expired. Send a new one.
              </p>
            )}
            {codeId && !expired && expiresAt && (
              <p className="mt-1.5 font-mono text-[11px] text-gray-400">
                Sent to {issuedTo.length} address{issuedTo.length === 1 ? '' : 'es'} · expires in {mmss(expiresAt - now)} · single use
              </p>
            )}
          </div>

          {submitError && (
            <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] font-semibold text-rose-700">
              {submitError}
            </div>
          )}

          {offlineRefused && (
            <div role="alert" className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-800">
              <span className="font-semibold">
                {registerName} is offline — a destructive command cannot be delivered right now, and it was refused rather than queued silently.
              </span>
              <button
                type="button"
                onClick={() => fire(true)}
                disabled={isBusy}
                className="flex min-h-[44px] items-center justify-center rounded-lg border border-amber-500 bg-white text-[13px] font-bold text-amber-800 disabled:opacity-50"
              >
                {isBusy ? 'Arming…' : 'Arm on next contact'}
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isBusy}
              className="h-[44px] flex-1 rounded-lg border border-gray-300 bg-white text-[13px] font-semibold text-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            {!offlineRefused && (
              <button
                type="button"
                onClick={() => fire(false)}
                disabled={!canFire || isBusy}
                className="h-[44px] flex-1 rounded-lg bg-rose-600 text-[13px] font-bold text-white disabled:opacity-45"
              >
                {isBusy ? 'Working…' : FIRE_LABEL[action] || `Run ${actionLabel}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
